import { datadogRum } from "@datadog/browser-rum";
import { IIdentityRepository } from "../../repositories/IdentityRepository";
import { LoginSettings } from "../../domain/LoginSettings";
import { DeveloperSettings } from "../../domain/DeveloperSettings";
import {
    IOrganizationSettings,
    OrganizationSettings as RestOrganizationSettings
} from "../../rest-model/OrganizationSettings";

export interface IOrganizationSettingsService {
    getOrganizationSettings({ organizationId }: { organizationId: string }): Promise<IOrganizationSettings>;
    getDeveloperSettings({ organizationId }: { organizationId: string }): Promise<DeveloperSettings>;
    getDeveloperSettingsForOrgs({
        organizationIds
    }: {
        organizationIds: string[];
    }): Promise<Record<string, DeveloperSettings>>;
    getLocalLoginSettings({ organizationId }: { organizationId: string }): Promise<LoginSettings>;
    updateGenAISettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean };
    }): Promise<{ enabled: boolean }>;
    updateLocalLoginSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: LoginSettings;
    }): Promise<LoginSettings>;
    updateDeveloperSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; vendorName: string };
    }): Promise<DeveloperSettings>;
    updateOrganizationSettings({
        organizationId,
        updates
    }: {
        organizationId: string;
        updates: Partial<RestOrganizationSettings>;
    }): Promise<RestOrganizationSettings>;
}

export class OrganizationSettingsService implements IOrganizationSettingsService {
    identityRepository: IIdentityRepository;

    constructor(identityRepository: IIdentityRepository) {
        this.identityRepository = identityRepository;
    }

    public async getLocalLoginSettings({ organizationId }: { organizationId: string }): Promise<LoginSettings> {
        const result = await this.identityRepository.getLocalLoginSettings({ organizationId });
        return new LoginSettings(result.Settings);
    }

    public async getOrganizationSettings({
        organizationId
    }: {
        organizationId: string;
    }): Promise<RestOrganizationSettings> {
        const result = await this.identityRepository.getOrganizationSettings({ organizationId });
        return new RestOrganizationSettings(result);
    }

    public async getDeveloperSettings({ organizationId }: { organizationId: string }): Promise<DeveloperSettings> {
        const settings = await this.getOrganizationSettings({ organizationId });
        return new DeveloperSettings(settings.developer);
    }

    /**
     * Returns developer settings for many orgs at once, keyed by organization id.
     *
     * TODO(EPTS-5662): this is a temporary fan-out over the per-org `GET /settings` endpoint (there is
     * no bulk read endpoint yet). Replace with a single bulk/searchable endpoint — e.g.
     * `searchDeveloperSettings({ search, page, pageSize })` returning orgs + settings + total count,
     * access-scoped server-side — once the backend exists, so org-name/vendor-name filtering and
     * pagination can move server-side.
     *
     * Requests are issued in bounded-concurrency chunks (parallel within a chunk, sequential across
     * chunks) so a support user with many accessible orgs can't fan out an unbounded burst of requests.
     */
    public async getDeveloperSettingsForOrgs({
        organizationIds
    }: {
        organizationIds: string[];
    }): Promise<Record<string, DeveloperSettings>> {
        // Conservative cap on in-flight requests per batch to avoid hammering the backend for support
        // users with many orgs. Not a measured backend limit — TODO: tune to the observed throttle
        // threshold once the bulk endpoint work surfaces real numbers.
        const CHUNK_SIZE = 25;
        const result: Record<string, DeveloperSettings> = {};

        for (let i = 0; i < organizationIds.length; i += CHUNK_SIZE) {
            const chunk = organizationIds.slice(i, i + CHUNK_SIZE);
            const entries = await Promise.all(
                chunk.map(async (organizationId) => {
                    try {
                        return [organizationId, await this.getDeveloperSettings({ organizationId })] as const;
                    } catch (error) {
                        // One org failing to load (e.g. 403/404/transient error) must not blank the
                        // whole list — fall back to safe defaults for that org so the rest still render.
                        // Log it so the failure is observable rather than indistinguishable from
                        // "disabled". Use a stable message so Datadog groups all failures for this
                        // call-site together, and attach the raw error via `cause` for the details.
                        datadogRum.addError(
                            new Error("getDeveloperSettingsForOrgs: failed to load org settings", { cause: error }),
                            { organizationId }
                        );
                        return [organizationId, new DeveloperSettings(undefined)] as const;
                    }
                })
            );
            for (const [organizationId, developer] of entries) {
                result[organizationId] = developer;
            }
        }

        return result;
    }

    public async updateGenAISettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean };
    }) {
        return await this.identityRepository.updateGenAISettings({ organizationId, settings });
    }

    public async updateDeveloperSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; vendorName: string };
    }): Promise<DeveloperSettings> {
        const result = await this.identityRepository.updateDeveloperSettings({ organizationId, settings });
        // The PUT may be a no-content (204) response, in which case the repository returns undefined.
        // Fall back to the submitted settings so the returned entity reflects what was actually saved
        // rather than the constructor's defaults.
        return new DeveloperSettings(result ?? settings);
    }

    public async updateOrganizationSettings({
        organizationId,
        updates
    }: {
        organizationId: string;
        updates: Partial<RestOrganizationSettings>;
    }): Promise<RestOrganizationSettings> {
        const result = await this.identityRepository.updateOrganizationSettings({
            organizationId,
            updates
        });
        return new RestOrganizationSettings(result);
    }

    public async updateLocalLoginSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: LoginSettings;
    }): Promise<LoginSettings> {
        const adaptedSettings = {
            enabled: settings?.localLoginEnabled,
            mfaEnabled: settings?.mfaEnabled
        };

        const result = await this.identityRepository.updateLocalLoginSettings({
            organizationId,
            settings: adaptedSettings
        });
        return new LoginSettings(result);
    }
}
